<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import { isEmpty } from '../../../../utils/CommonFunctions';
import IconComponent from '../../../../components/ui/common/IconComponent.vue';

const props = defineProps<{
  lat?: number | string;
  lng?: number | string;
  fullscreen: boolean;
}>();

const initialPosition = computed(() => {
  if (!isEmpty(props.lat) && !isEmpty(props.lng)) {
    return { lat: parseFloat(props.lat!.toString()), lng: parseFloat(props.lng!.toString()) };
  }
  return null;
});

const mapContainer = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const map = ref<google.maps.Map | null>(null);
const currentMarker = ref<google.maps.Marker | null>(null);
const destinationMarker = ref<google.maps.Marker | null>(null);
const routePath = ref<google.maps.Polyline | null>(null);
const directionsRenderer = ref<google.maps.DirectionsRenderer | null>(null);
const positions = ref<google.maps.LatLngLiteral[]>([]);
const watchId = ref<number | null>(null);
const searchBox = ref<google.maps.places.SearchBox | null>(null);
const isDestinationSet = ref(false);
const showDialog = ref(false);
const newPlaceName = ref('');
const tempMarker = ref<google.maps.Marker | null>(null);
const customMarkers = ref<{ marker: google.maps.Marker; name: string }[]>([]);
const isMarkerMode = ref(false);
const tempInfoWindow = ref<google.maps.InfoWindow | null>(null);

const GOOGLE_MAPS_API_KEY = 'AIzaSyBC8jOzS5ifnpBsK4QT6rfVNdxmO483m8w';

const loader = new Loader({
  apiKey: GOOGLE_MAPS_API_KEY,
  version: 'weekly',
  libraries: ['places']
});

const showRoute = async (origin: google.maps.LatLng, destination: google.maps.LatLng) => {
  const directionsService = new google.maps.DirectionsService();
  
  try {
    const response = await directionsService.route({
      origin: origin,
      destination: destination,
      travelMode: google.maps.TravelMode.DRIVING
    });

    if (!directionsRenderer.value) {
      directionsRenderer.value = new google.maps.DirectionsRenderer({
        map: map.value,
        suppressMarkers: true
      });
    }

    directionsRenderer.value.setDirections(response);
  } catch (error) {
    console.error('Error calculating route:', error);
    alert('Could not calculate route to destination');
  }
};

const createCoordinateContent = (position: google.maps.LatLng) => {
  return `
    <div class="coordinate-info">
      <p>Latitude: ${position.lat().toFixed(6)}</p>
      <p>Longitude: ${position.lng().toFixed(6)}</p>
      <button onclick="document.dispatchEvent(new Event('save-location'))" class="save-location-btn">
        Save Location
      </button>
    </div>
  `;
};

const addMarkerAtCurrentLocation = () => {
  if (!currentMarker.value) {
    alert('Current location not available');
    return;
  }

  if (tempMarker.value) {
    tempMarker.value.setMap(null);
  }

  tempMarker.value = new google.maps.Marker({
    position: currentMarker.value.getPosition(),
    map: map.value,
    draggable: true,
    icon: {
      url: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
    }
  });

  tempMarker.value.addListener('dragend', () => {
    const position = tempMarker.value!.getPosition()!;
    
    if (tempInfoWindow.value) {
      tempInfoWindow.value.close();
    }

    tempInfoWindow.value = new google.maps.InfoWindow({
      content: createCoordinateContent(position)
    });

    tempInfoWindow.value.open(map.value, tempMarker.value);
  });
};

const handleMapClick = (event: google.maps.MapMouseEvent) => {
  if (!isMarkerMode.value || !event.latLng) return;
  
  if (tempMarker.value) {
    tempMarker.value.setPosition(event.latLng);
  } else {
    tempMarker.value = new google.maps.Marker({
      position: event.latLng,
      map: map.value,
      draggable: true,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
      }
    });

    tempMarker.value.addListener('dragend', () => {
      const position = tempMarker.value!.getPosition()!;
      
      if (tempInfoWindow.value) {
        tempInfoWindow.value.close();
      }

      tempInfoWindow.value = new google.maps.InfoWindow({
        content: createCoordinateContent(position)
      });

      tempInfoWindow.value.open(map.value, tempMarker.value);
    });
  }

  if (tempInfoWindow.value) {
    tempInfoWindow.value.close();
  }

  tempInfoWindow.value = new google.maps.InfoWindow({
    content: createCoordinateContent(event.latLng)
  });

  tempInfoWindow.value.open(map.value, tempMarker.value);
};

const toggleMarkerMode = () => {
  isMarkerMode.value = !isMarkerMode.value;
  if (!isMarkerMode.value) {
    if (tempMarker.value) {
      tempMarker.value.setMap(null);
      tempMarker.value = null;
    }
    if (tempInfoWindow.value) {
      tempInfoWindow.value.close();
      tempInfoWindow.value = null;
    }
  }
};

const savePlace = () => {
  if (!tempMarker.value || !newPlaceName.value.trim()) return;

  const marker = new google.maps.Marker({
    position: tempMarker.value.getPosition(),
    map: map.value,
    title: newPlaceName.value,
    icon: {
      url: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
    }
  });

  const infoWindow = new google.maps.InfoWindow({
    content: newPlaceName.value
  });

  marker.addListener('click', () => {
    infoWindow.open(map.value, marker);
  });

  customMarkers.value.push({
    marker,
    name: newPlaceName.value
  });

  tempMarker.value.setMap(null);
  tempMarker.value = null;
  if (tempInfoWindow.value) {
    tempInfoWindow.value.close();
    tempInfoWindow.value = null;
  }
  newPlaceName.value = '';
  showDialog.value = false;
  isMarkerMode.value = false;
};

const cancelPlace = () => {
  if (tempMarker.value) {
    tempMarker.value.setMap(null);
    tempMarker.value = null;
  }
  if (tempInfoWindow.value) {
    tempInfoWindow.value.close();
    tempInfoWindow.value = null;
  }
  newPlaceName.value = '';
  showDialog.value = false;
  isMarkerMode.value = false;
};

const updatePosition = (position: GeolocationPosition) => {
  const newPosition = {
    lat: position.coords.latitude,
    lng: position.coords.longitude
  };

  const motorcycleIcon = {
    url: 'https://maps.google.com/mapfiles/ms/icons/motorcycling.png',
    scaledSize: new google.maps.Size(40, 40),
    anchor: new google.maps.Point(20, 20)
  };

  if (!currentMarker.value && map.value) {
    currentMarker.value = new google.maps.Marker({
      position: newPosition,
      map: map.value,
      title: 'Current Location',
      icon: motorcycleIcon
    });
  } else if (currentMarker.value) {
    currentMarker.value.setPosition(newPosition);
    currentMarker.value.setIcon(motorcycleIcon);
  }

  positions.value.push(newPosition);
  if (routePath.value) {
    routePath.value.setPath(positions.value);
  }

  if (destinationMarker.value) {
    showRoute(
      new google.maps.LatLng(newPosition.lat, newPosition.lng),
      destinationMarker.value.getPosition()!
    );
  }

  map.value?.setCenter(newPosition);
};

const initMap = async () => {
  try {
    const google = await loader.load();
    const defaultLocation = initialPosition.value || { lat: 0, lng: 0 };

    map.value = new google.maps.Map(mapContainer.value!, {
      center: defaultLocation,
      zoom: 15,
      mapTypeId: 'roadmap',
      disableDefaultUI: false,
      mapTypeControl: false
    });

    map.value.addListener('click', handleMapClick);

    document.addEventListener('save-location', () => {
      if (tempMarker.value) {
        //showDialog.value = true;
        let val = tempMarker.value ? tempMarker.value.getPosition() : '';
        if (val !== '') {
          let pos = val?.toString();
          if (pos) {
            const [lat, lng] = pos.replace(/[()]/g, '').split(', ').map(Number);
            emit('save', lat, lng);
          }
        }
      }
    });

    searchBox.value = new google.maps.places.SearchBox(searchInput.value!);

    map.value.addListener('bounds_changed', () => {
      searchBox.value?.setBounds(map.value?.getBounds() as google.maps.LatLngBounds);
    });

    searchBox.value.addListener('places_changed', () => {
      const places = searchBox.value?.getPlaces();
      if (!places || places.length === 0) return;

      const place = places[0];
      if (!place.geometry || !place.geometry.location) return;

      if (destinationMarker.value) {
        destinationMarker.value.setMap(null);
      }

      if (directionsRenderer.value) {
        directionsRenderer.value.setMap(null);
      }

      destinationMarker.value = new google.maps.Marker({
        map: map.value,
        position: place.geometry.location,
        title: place.name,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
        }
      });

      map.value?.setCenter(place.geometry.location);
      map.value?.setZoom(15);

      isDestinationSet.value = true;

      if (currentMarker.value) {
        showRoute(currentMarker.value.getPosition()!, place.geometry.location);
      }
    });

    routePath.value = new google.maps.Polyline({
      path: [],
      geodesic: true,
      strokeColor: '#FF0000',
      strokeOpacity: 1.0,
      strokeWeight: 2,
      map: map.value
    });

    if (initialPosition.value) {
      destinationMarker.value = new google.maps.Marker({
        position: initialPosition.value,
        map: map.value,
        title: 'Destination',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
        }
      });

      isDestinationSet.value = true;
      startTracking();
    } else {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const currentLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            const motorcycleIcon = {
              url: 'https://maps.google.com/mapfiles/ms/icons/motorcycling.png',
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 20)
            };
            
            currentMarker.value = new google.maps.Marker({
              position: currentLocation,
              map: map.value,
              title: 'Current Location',
              icon: motorcycleIcon
            });

            map.value?.setCenter(currentLocation);
          },
          (error) => console.error('Error getting current location:', error)
        );
      }
    }
  } catch (error) {
    console.error('Error loading Google Maps:', error);
  }
};

const startTracking = () => {
  if (!isDestinationSet.value) {
    alert('Please search and select a destination first');
    return;
  }

  if ('geolocation' in navigator) {
    watchId.value = navigator.geolocation.watchPosition(
      updatePosition,
      (error) => console.error('Error getting location:', error),
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  } else {
    console.error('Geolocation is not supported by this browser.');
  }
};

const stopTracking = () => {
  if (watchId.value !== null) {
    navigator.geolocation.clearWatch(watchId.value);
    watchId.value = null;
  }
};

const resetTracking = () => {
  stopTracking();
  if (currentMarker.value) {
    currentMarker.value.setMap(null);
    currentMarker.value = null;
  }
  if (destinationMarker.value) {
    destinationMarker.value.setMap(null);
    destinationMarker.value = null;
  }
  if (routePath.value) {
    routePath.value.setPath([]);
  }
  if (directionsRenderer.value) {
    directionsRenderer.value.setMap(null);
    directionsRenderer.value = null;
  }
  if (tempInfoWindow.value) {
    tempInfoWindow.value.close();
    tempInfoWindow.value = null;
  }
  customMarkers.value.forEach(({ marker }) => marker.setMap(null));
  customMarkers.value = [];
  if (tempMarker.value) {
    tempMarker.value.setMap(null);
    tempMarker.value = null;
  }
  positions.value = [];
  isDestinationSet.value = false;
  isMarkerMode.value = false;
  if (searchInput.value) {
    searchInput.value.value = '';
  }
};

onMounted(() => {
  initMap();
});

onUnmounted(() => {
  stopTracking();
  document.removeEventListener('save-location', () => {});
});


const emit = defineEmits<{
  (e: 'save', lat: number, lng: number): void
  (e: 'close'): void
}>()



watch(() => props.lat || props.lng, (_newVal) => {
    if (props.lat && props.lng) {
        console.log('map init ')
        initMap();
    }
})

watch(() => props.fullscreen, (_newVal) => {
    let mapContainer = document.getElementById('map-container');
    if (props.fullscreen && mapContainer) {      
        mapContainer.style.position = 'fixed';
        mapContainer.style.top = '0';
        mapContainer.style.left = '0';
        mapContainer.style.right = '0';
        mapContainer.style.bottom = '0';        
    } else if (mapContainer) {
      mapContainer.style.position = '';
      mapContainer.style.top = '';
      mapContainer.style.left = '';
      mapContainer.style.right = '';
      mapContainer.style.bottom = '';        
    }
})


</script>

<template>
  <div class="map-container" id="map-container">
    <div class="search-box">
      <input
        ref="searchInput"
        type="text"
        placeholder="Search for a destination..."
        class="search-input"
      />
    </div>
    <div ref="mapContainer" class="map"></div>
    <div class="fab-container">
      <button 
        @click="startTracking" 
        :disabled="watchId !== null || !isDestinationSet"
        class="fab"
        title="Start Tracking"
      >
      <IconComponent name="CirclePlay" />
      </button>
      <button 
        @click="stopTracking" 
        :disabled="watchId === null"
        class="fab"
        title="Stop Tracking"
      >
      <IconComponent name="CircleStop" />
      </button>
      <button 
        @click="resetTracking" 
        class="fab reset-button"
        title="Reset"
      >
        <IconComponent name="RotateCcw" />
      </button>
      <button 
        @click="toggleMarkerMode" 
        :class="['fab', { active: isMarkerMode }]"
        class="marker-button"
        title="Add Marker"
      >
      <IconComponent name="MapPinPlus" />
      </button>
      <button 
        v-if="isMarkerMode"
        @click="addMarkerAtCurrentLocation" 
        class="fab current-location-button"
        title="Mark Current Location"
      >
      <IconComponent name="Locate" />
      </button>
      <button 
        @click="$emit('close')" 
        class="fab bg-gray"
        title="Close"
      >
      <IconComponent name="LogOut" />
      </button>
    </div>

    <!-- Place Name Dialog -->
    <div v-if="showDialog" class="dialog-overlay">
      <div class="dialog">
        <h3>Add New Place</h3>
        <p class="dialog-hint">Drag the marker to adjust its position</p>
        <input
          v-model="newPlaceName"
          type="text"
          placeholder="Enter place name"
          class="dialog-input"
          @keyup.enter="savePlace"
        />
        <div class="dialog-buttons">
          <button @click="savePlace" class="save-button">Save</button>
          <button @click="cancelPlace" class="cancel-button">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.map-container {
  width: 100%;
  height: 100vh;
  position: relative;
}

.map {
  width: 100%;
  height: 100%;
}

.search-box {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  width: 90%;
  max-width: 400px;
  padding: 0 10px;
  box-sizing: border-box;
}

.search-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  font-size: 16px;
  -webkit-appearance: none;
  appearance: none;
}

.fab-container {
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #4CAF50;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s, box-shadow 0.2s;
  -webkit-tap-highlight-color: transparent;
}

.fab:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.fab.reset-button {
  background-color: #f44336;
}

.fab.marker-button {
  background-color: #2196F3;
}

.fab.marker-button.active {
  background-color: #FFC107;
  color: #000;
}

.fab.current-location-button {
  background-color: #9C27B0;
}

.fab:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

.fab:active:not(:disabled) {
  transform: scale(0.95);
}

/* Dialog styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.dialog {
  background-color: #a9a9a9;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.dialog h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1.2rem;
}

.dialog-hint {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 0.9rem;
  font-style: italic;
}

.dialog-input {
  width: 100%;
  padding: 12px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
}

.dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.save-button {
  background-color: #4CAF50;
}

.cancel-button {
  background-color: #9e9e9e;
}

/* InfoWindow styles */
:global(.coordinate-info) {
  padding: 12px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background-color: #656666;
}

:global(.coordinate-info p) {
  margin: 4px 0;
  font-size: 14px;
  color: #1f2937;
}

:global(.save-location-btn) {
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:global(.save-location-btn:hover) {
  opacity: 0.9;
}

:global(.save-location-btn:active) {
  transform: scale(0.98);
}

/* Google Maps InfoWindow styles override */
:global(.gm-style-iw) {
  background-color: #656666 !important;
  padding: 0 !important;
}

:global(.gm-style-iw-d) {
  overflow: hidden !important;
}

:global(.gm-style-iw button) {
  background-color: #656666 !important;
  top: 0 !important;
  right: 0 !important;
}

:global(.gm-style-iw button img) {
  filter: brightness(0) invert(1);
}

:global(.gm-style-iw-tc::after) {
  background: #656666 !important;
}

/* Mobile-specific styles */
@media (max-width: 480px) {
  .search-box {
    top: 10px;
    padding: 0 15px;
  }

  .fab-container {
    bottom: 15px;
    right: 15px;
  }

  .fab {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .dialog {
    padding: 15px;
  }

  .dialog h3 {
    font-size: 1.1rem;
  }

  .dialog-input {
    padding: 10px;
    font-size: 14px;
  }
}

/* iOS Safari specific styles */
@supports (-webkit-touch-callout: none) {
  .search-input,
  .dialog-input {
    font-size: 16px;
  }
}
</style>